import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useRef } from "react";
import { AuthProvider, useAuth } from "@/lib/auth.jsx";
import { api } from "@/lib/api";
import { syncProgress } from "@/lib/progress";
import { Toaster } from "sonner";
import LandingPreview from "@/pages/LandingPreview";
import Mindset from "@/pages/Mindset";
import AuthPage from "@/pages/AuthPage";
import Quiz from "@/pages/Quiz";
import Dashboard from "@/pages/Dashboard";
import OpportunityDetail from "@/pages/OpportunityDetail";
import Coach from "@/pages/Coach";
import ExitCalculator from "@/pages/ExitCalculator";
import Roadmap from "@/pages/Roadmap";
import Compare from "@/pages/Compare";
import Paywall from "@/pages/Paywall";
import PaymentResult from "@/pages/PaymentResult";
import GetMeOut from "@/pages/GetMeOut";
import CheckIn from "@/pages/CheckIn";
import Saved from "@/pages/Saved";
import Referral from "@/pages/Referral";
import Terms from "@/pages/Terms";
import Recaps from "@/pages/Recaps";
import Plan from "@/pages/Plan";
import Account from "@/pages/Account";
import MyWork from "@/pages/MyWork";
import StartPage from "@/pages/StartPage";
import Unsubscribe from "@/pages/Unsubscribe";
import Admin from "@/pages/Admin";
import ResetPassword from "@/pages/ResetPassword";
import Nav from "@/components/Nav";
import NudgeBanner from "@/components/NudgeBanner";
import "@/App.css";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-8 text-gray-400">Loading…</div>;
  if (!user) return <Navigate to="/auth" replace />;
  return children;
}

function AdminOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-8 text-gray-400">Loading…</div>;
  if (!user) return <Navigate to="/auth" replace />;
  if (!user.is_admin) return <Navigate to="/dashboard" replace />;
  return children;
}

function NudgeInScope() {
  const { user } = useAuth();
  if (!user) return null;
  return <NudgeBanner />;
}

// Saves the current screen and takes signed-in users back where they left off.
function ProgressManager() {
  const { user, loading } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const resumed = useRef(false);

  useEffect(() => {
    if (!user) return;
    const p = loc.pathname;
    if (p === "/" || p === "/auth" || p === "/reset-password" || p.startsWith("/payment")) return;
    syncProgress({ last_route: p });
  }, [user, loc.pathname]);

  useEffect(() => {
    if (loading || !user || resumed.current) return;
    resumed.current = true;
    if (loc.pathname !== "/") return;
    api.get("/resume").then(({ data }) => {
      const r = data.destination;
      if (r && r !== "/" && r !== "/auth") nav(r, { replace: true });
    }).catch(() => {});
  }, [loading, user]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
}

export default function App() {
  return (
    <div className="App grain min-h-screen relative">
      <AuthProvider>
        <BrowserRouter>
          <Nav />
          <NudgeInScope />
          <ProgressManager />
          <div className="relative z-10 pb-32">
            <Routes>
              <Route path="/" element={<LandingPreview />} />
              <Route path="/landing-preview" element={<LandingPreview />} />
              <Route path="/start" element={<StartPage />} />
              <Route path="/unsubscribe" element={<Unsubscribe />} />
              <Route path="/auth" element={<AuthPage />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/quiz" element={<Quiz />} />
              <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
              <Route path="/opportunity/:id" element={<Protected><OpportunityDetail /></Protected>} />
              <Route path="/coach" element={<Protected><Coach /></Protected>} />
              <Route path="/exit" element={<Protected><ExitCalculator /></Protected>} />
              <Route path="/roadmap" element={<Protected><Roadmap /></Protected>} />
              <Route path="/compare" element={<Protected><Compare /></Protected>} />
              <Route path="/pro" element={<Protected><Paywall /></Protected>} />
              <Route path="/payment/success" element={<Protected><PaymentResult status="success" /></Protected>} />
              <Route path="/payment/cancel" element={<Protected><PaymentResult status="cancel" /></Protected>} />
              <Route path="/get-me-out" element={<Protected><GetMeOut /></Protected>} />
              <Route path="/checkin" element={<Protected><CheckIn /></Protected>} />
              <Route path="/saved" element={<Protected><Saved /></Protected>} />
              <Route path="/referral" element={<Protected><Referral /></Protected>} />
              <Route path="/recaps" element={<Protected><Recaps /></Protected>} />
              <Route path="/plan" element={<Protected><Plan /></Protected>} />
              <Route path="/account" element={<Protected><Account /></Protected>} />
              <Route path="/work" element={<Protected><MyWork /></Protected>} />
              <Route path="/work/:id" element={<Protected><MyWork /></Protected>} />
              <Route path="/mindset" element={<Protected><Mindset /></Protected>} />
              <Route path="/admin" element={<AdminOnly><Admin /></AdminOnly>} />
              <Route path="/terms" element={<Terms />} />
            </Routes>
          </div>
          <Toaster position="top-center" theme="dark" />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}
